import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiRoute } from "services";
import API from "services/lib/api";
import {
    RECEIVE_MESSAGE,
    SEND_AGENT_REPLY,
    SUBSCRIBE_TO_TICKET,
    MARK_AS_READ,
    MESSAGE_RECALLED,
} from "services/socket/events";
import {
    clearTicketMessages,
    deleteTicketsMessages,
    saveTicketsMessages,
    setTicketMessages,
    updateTicketMessage,
} from "store/tickets/actions";
import { dataQueryStatus } from "utils";
import {
    extractObjectKeyValueFromArray,
    generateID,
    getErrorMessage,
    sortTickets,
} from "utils/helper";
import LiveChatInput from "./LiveChatInput/LiveChatInput";
import MessageBody from "./MessageBody/MessageBody";
import {
    appMessageUserTypes,
    messageTypes,
} from "./MessageBody/Messages/Message/enums";
import { selectNextTicket } from "../../CustomerTicketsContainer/CustomerTickets/util/helper";
import { deleteIncomingTicket } from "store/tickets/incomingTickets/actions";
import { setActiveTicket } from "store/tickets/actions";
import { MESSAGE_STATUSES } from "./enum";
import { useSocket } from "services/socket/hook";
import { useDoesTicketMatchFilters } from "hooks/useDoesTicketMatchFilters";

const { LOADING, ERROR, DATAMODE } = dataQueryStatus;
const { WORKSPACE_AGENT, THIRD_USER } = appMessageUserTypes;
const { DEFAULT } = messageTypes;

const { SENDING, FAILED, SENT, RESENDING } = MESSAGE_STATUSES;

const LiveChat = ({
    setRecentCustomerMessage,
    setTicketData,
    reconnectUser,
    cannedMessage,
    setCannedMessage,
    showFilters,
    setInvestigativeCustomerMessage,
    setFetchMessageStatus,
    selectedCustomerMessage,
    selectCustomerMessage,
}) => {
    const [adjustTextFieldSize, toggleTextFieldSize] = useState(false);
    const { ticketsMessages, activeTicket } = useSelector(
        (state) => state.tickets
    );
    const doesTicketMatchFilters = useDoesTicketMatchFilters();
    const { incomingTickets } = useSelector((state) => state.incomingTickets);
    const { ticketsfilterRequest } = useSelector((state) => state?.ticketsFilters);
    const { lastModified, keywordSearch } = ticketsfilterRequest;

    const { ticketId, customer } = activeTicket || {};

    const messages = useMemo(() => {
        if (!ticketId) {
            return [];
        }
        return (
            ticketsMessages?.filter((item) => item?.ticketId === ticketId) ??
            []
        );
    }, [ticketsMessages, ticketId]);

    const lastAgentMessage = useMemo(() => {
        if (!messages?.length) {
            return null;
        }

        for (let index = messages.length - 1; index >= 0; index -= 1) {
            const currentMessage = messages[index];
            if (currentMessage?.senderType === WORKSPACE_AGENT) {
                return currentMessage;
            }
        }

        return null;
    }, [messages]);

    // const [status, setStatus] = useState(LOADING);
    const [errorMssg, setErrorMssg] = useState("");
    const [unsentMsgs, setUnsetMsgs] = useState([]);
    const [recallToast, setRecallToast] = useState(null);
    const [isRecallingMessage, setIsRecallingMessage] = useState(false);
    const recallToastTimerRef = useRef(null);

    const socket = useSocket();

    const dispatch = useDispatch();

    const clearRecallToast = () => {
        if (recallToastTimerRef.current) {
            clearTimeout(recallToastTimerRef.current);
            recallToastTimerRef.current = null;
        }
        setRecallToast(null);
    };

    const getToastPreviewText = (text, attachments = []) => {
        if (typeof text === "string" && text.trim().length > 0) {
            return text.trim();
        }

        if (attachments?.length > 1) {
            return "Sent attachments";
        }

        if (attachments?.length === 1) {
            return "Sent an attachment";
        }

        return "Message sent";
    };

    const showRecallToast = (previewText) => {
        clearRecallToast();
        setRecallToast({
            previewText: previewText?.slice(0, 80) || "Message sent",
        });
        recallToastTimerRef.current = setTimeout(() => {
            clearRecallToast();
        }, 6000);
    };

    const handleRecallLastMessage = async () => {
        if (!ticketId || isRecallingMessage) {
            return;
        }
        try {
            setIsRecallingMessage(true);
            await API.delete(apiRoute.recallLastAgentMessage(ticketId));
            const recalledMessageId = lastAgentMessage?.messageId;
            if (recalledMessageId) {
                dispatch(
                    deleteTicketsMessages({
                        ticketId,
                        messageId: recalledMessageId,
                    })
                );
                pruneMessageFromTicketData(recalledMessageId);
                setUnsetMsgs((prev) =>
                    prev.filter((id) => id !== recalledMessageId)
                );
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsRecallingMessage(false);
        }
    };

    const handleUndoSend = async () => {
        if (!recallToast) {
            return;
        }
        clearRecallToast();
        await handleRecallLastMessage();
    };

    const pruneMessageFromTicketData = (messageIdToRemove) => {
        if (typeof setTicketData !== "function") {
            return;
        }

        setTicketData((prevMessages) => {
            if (Array.isArray(prevMessages)) {
                return prevMessages.filter(
                    (message) => message?.messageId !== messageIdToRemove
                );
            }

            if (prevMessages && Array.isArray(prevMessages?.messages)) {
                return {
                    ...prevMessages,
                    messages: prevMessages.messages.filter(
                        (message) => message?.messageId !== messageIdToRemove
                    ),
                };
            }

            return prevMessages;
        });
    };

    // const [disabledInput, setDisabledInput] = useState(false);

    const updateUnsentMsgs = (messageId, shouldDelete) => {
        if (shouldDelete) {
            setUnsetMsgs(unsentMsgs.filter((x) => x !== messageId));
        } else {
            setUnsetMsgs([...unsentMsgs, messageId]);
        }
    };

    const setStatus = (status) => {
        // console.log("status", status);
    }

    const requestAllMessages = async (
        httpRequest,
        suspendFetchMessageStatus
    ) => {
        try {
            if (!ticketId) {
                return;
            }
            setStatus(LOADING);
            if (!suspendFetchMessageStatus) {
                setFetchMessageStatus?.(LOADING);
            }
            setErrorMssg();
            const url = apiRoute?.getTicketMessages(ticketId);
            const res = await API.get(url, {
                signal: httpRequest?.signal,
            });
            if (res.status === 200) {
                const { data } = res.data;
                setStatus(DATAMODE);

                const messagesArr = data.map((x) => ({
                    ...x,
                    fileAttachments:
                        x?.fileAttachments?.length > 0
                            ? x?.fileAttachments
                            : x?.form?.formElement?.media?.map((media) => ({
                                fileAttachmentUrl: media?.link,
                                fileAttachmentType: media?.type,
                                fileAttachmentName: media?.mediaName,
                            })),
                }));

                dispatch(setTicketMessages(messagesArr));

                setTicketData(messagesArr);

                let investigativeMessages = extractObjectKeyValueFromArray(
                    data?.filter(
                        (message) =>
                            message?.messageType === DEFAULT &&
                            message?.senderType === THIRD_USER
                    ),
                    "messageContent"
                );
                setInvestigativeCustomerMessage(investigativeMessages);

                let recentInputtedMessage = [...data]
                    .reverse()
                    ?.find(
                        (message) =>
                            message.receiverType === WORKSPACE_AGENT &&
                            message?.messageType === messageTypes?.DEFAULT
                    );
                setRecentCustomerMessage(recentInputtedMessage);
                setUnsetMsgs([]);
                setFetchMessageStatus?.(DATAMODE);
            }
        } catch (err) {
            // Ignore errors from aborted requests (when switching tickets/tabs)
            const isAborted = err.name === 'CanceledError' || 
                             err.name === 'AbortError' || 
                             err.code === 'ERR_CANCELED' ||
                             (err.message && err.message.includes('canceled')) ||
                             httpRequest?.signal?.aborted;
            
            if (isAborted) {
                // Request was intentionally cancelled, don't show error
                return;
            }

            console.log(errorMssg)
            setStatus(ERROR);
            setFetchMessageStatus?.(ERROR);
            setErrorMssg(getErrorMessage(err));
            if (err.response?.data?.code === "TICKET_CLOSED") {
                const filteredTickets = incomingTickets?.filter(ticket => doesTicketMatchFilters(ticket));
                const visibleTickets = sortTickets(filteredTickets, lastModified);
            
                selectNextTicket(
                    activeTicket,
                    visibleTickets,
                    (ticket, ticketId) => {
                        dispatch(setActiveTicket(ticket));
                        // === null ? sortedTickets[0] : ticket
                        ticketId &&
                            dispatch(deleteIncomingTicket({ ticketId }));
                    }
                );
            }
        }
    };

    const sendSocketEvent = (event, data, callBack) => {
        let socketResponse = null;
        let timeOut = null;
        socket?.connect();

        const socketEvent = socket?.emit(event, { ...data }, (resp) => {
            socketResponse = resp;
            clearTimeout(timeOut);
            return callBack(socketResponse, socketEvent);
        });

        timeOut = setTimeout(() => {
            callBack?.(socketResponse, socketEvent);
        }, 8000);
    };

    const handleNewMessage = async (request, existingMessageData) => {
        let { message, fileAttachments } = request;

        if (message === "" && fileAttachments.length === 0) {
            return "";
        }

        const newMessageId = generateID();

        if (!socket?.connected) {
            await socket?.connect();
        }

        // message = message?.replace?.(/[^\w ]/g, "");
        const { messageContent, messageId, ...otherData } =
            existingMessageData || {};

        let socReq = {
            ticketId,
            message: messageContent || message,
            messageType: DEFAULT,
            messageContentId: newMessageId,
            fileAttachments,
            ...otherData,
        };

        if (!messageId) {
            dispatch(
                saveTicketsMessages({
                    ticketId,
                    senderType: WORKSPACE_AGENT,
                    messageContentId: newMessageId,
                    messageId: newMessageId,
                    messageContent: message,
                    messageType: DEFAULT,
                    fileAttachments,
                    status: SENDING,
                })
            );
            const previewText = getToastPreviewText(message, fileAttachments);
            showRecallToast(previewText);
        } else {
            dispatch(
                updateTicketMessage({
                    messageId: messageId,
                    messageContentId: newMessageId,
                    ticketId,
                    status: RESENDING,
                })
            );
        }

        const compMessageId = messageId ? messageId : newMessageId;

        updateUnsentMsgs(compMessageId);

        const socketEventCallBack = (socketResponse, socketEvent, data) => {
            if (socketResponse?.status !== "ok") {
                dispatch(
                    updateTicketMessage({
                        messageId: compMessageId,
                        messageContentId: compMessageId,
                        ticketId,
                        status: FAILED,
                    })
                );
                updateUnsentMsgs(compMessageId);
                clearRecallToast();
            } else {
                dispatch(
                    updateTicketMessage({
                        messageId: compMessageId,
                        messageContentId: compMessageId,
                        ticketId,
                        status: SENT,
                    })
                );
                updateUnsentMsgs(compMessageId, true);
            }
        };

        sendSocketEvent(SEND_AGENT_REPLY, socReq, socketEventCallBack);
    };

    const handleMarkAsRead = async (messageId) => {
        await socket?.emit(MARK_AS_READ, {
            messageId,
        });
    };

    const createNewMessage = (message, read) => {
        dispatch(
            saveTicketsMessages({
                ...message,
                ticketId: message?.ticket?.ticketId,
                fileAttachments:
                    message?.fileAttachments?.length > 0
                        ? message?.fileAttachments
                        : message?.form?.formElement?.media?.map(
                            (media) => ({
                                fileAttachmentUrl: media?.link,
                                fileAttachmentType: media?.type,
                                fileAttachmentName: media?.mediaName,
                            })
                        ),
                readDate: read && ticketId === message?.ticket?.ticketId &&
                    new Date().toISOString(),
            })
        );
    }

    const handleReceive = (message) => {
        console.log("the message", message);

        if (message?.senderType === WORKSPACE_AGENT) {
            if (message?.messageContentId !== "") {
                dispatch(
                    updateTicketMessage({
                        messageContentId: message?.messageContentId,
                        ticketId: message?.ticket?.ticketId,
                        ...message
                    })
                );
            } else {
                createNewMessage(message)
            }
        } else if (
            message?.senderType === THIRD_USER
        ) {
            if (ticketId === message?.ticket?.ticketId) {
                handleMarkAsRead(message?.messageId);
            }
            createNewMessage(message, true)
        }


    };

    const handleMessageRecalled = (payload) => {
        const { ticketId: recalledTicketId, messageId } = payload || {};

        if (!recalledTicketId || !messageId) {
            return;
        }

        dispatch(
            deleteTicketsMessages({
                ticketId: recalledTicketId,
                messageId,
            })
        );

        if (recalledTicketId === ticketId) {
            pruneMessageFromTicketData(messageId);
            setUnsetMsgs((prev) => prev.filter((id) => id !== messageId));
            clearRecallToast();
        }
    };

    useEffect(() => {
        requestAllMessages();
        setCannedMessage?.("");
        return () => {
            dispatch(clearTicketMessages(ticketId));
            // socket?.off(CLOSED_TICKET);
        };
        //eslint-disable-next-line
    }, [activeTicket]);

    useEffect(() => {
        socket?.emit(SUBSCRIBE_TO_TICKET, { ticketId });
        socket?.on(RECEIVE_MESSAGE, handleReceive);
        socket?.on(MESSAGE_RECALLED, handleMessageRecalled);
        // socket?.on(CLOSED_TICKET, handleTicketClosure);
        // socket?.on("connect_error", handleSocketStatus);
        // socket?.on("disconnect", handleSocketStatus);
        // socket?.on("connect", handleSocketStatus);
        // socket?.on("error", handleSocketStatus);
        return () => {
            socket?.off(RECEIVE_MESSAGE);
            socket?.off(MESSAGE_RECALLED);
            // socket?.off(CLOSED_TICKET);
        };
        // eslint-disable-next-line
    }, [socket]);

    useEffect(() => {
        var objDiv = document.getElementById("chatBox");
        objDiv.scrollTop = objDiv.scrollHeight;
    }, [messages]);

    useEffect(() => {
        return () => {
            clearRecallToast();
        };
    }, []);

    // useEffect(() => {
    //     const httpRequest = new AbortController();
    //     if (networkStatus === ONLINE && unsentMsgs?.length === 0) {
    //         requestAllMessages(httpRequest, true);
    //     }

    //     if (networkStatus === RECONNECTING) {
    //         handleReconnectUser();
    //     }

    //     return () => {
    //         httpRequest?.abort();
    //     };

    //     //eslint-disable-next-line
    // }, [networkStatus]);

    // useEffect(() => {
    //     const delayDebounceFn = setTimeout(() => {
    //         !socket?.connected && socket?.connect()
    //     }, 3000);

    //     return () => clearTimeout(delayDebounceFn);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [socket])



    return (
        <div
            className={`abs__container ${adjustTextFieldSize ? "size-up" : ""
                } ${!showFilters ? "increase__height" : ""}`}>
            <div
                className={`chat__box  ${!showFilters ? "scrollable-collasped-y" : "scrollable-y"
                    }`}
                id='chatBox'>
           
                <MessageBody
                    {...{
                        messages,
                        customer,
                        selectedCustomerMessage,
                        selectCustomerMessage,
                        keywordSearch,
                    }}
                    handleResendMessage={handleNewMessage}
                />
            </div>
            <LiveChatInput
                {...{
                    ticketId,
                    handleNewMessage,
                    cannedMessage,
                    setCannedMessage,
                    adjustTextFieldSize,
                    toggleTextFieldSize,
                }}
                // disabledInput={disabledInput || status === ERROR}
            />
            {recallToast && (
                <div
                    className='undo__toast'
                    style={{
                        position: "fixed",
                        left: "50%",
                        bottom: "24px",
                        transform: "translateX(-50%)",
                        zIndex: 9999,
                    }}>
                    <span>{recallToast.previewText}</span>
                    <button
                        type='button'
                        className='undo__toast-btn'
                        onClick={handleUndoSend}>
                        Undo
                    </button>
                </div>
            )}
        </div>
    );
};

export default LiveChat;
